import { useEffect, useState, type ChangeEvent } from "react";
import { api, type Employee, type WorkSchedule, type DayHours } from "../lib/api";
import { useClientContext } from "../lib/ClientContext";
import { mitarbeiterSpalten } from "../lib/mitarbeiter-spalten";

export function SettingsEmployees(props: { onGoToClients?: () => void }) {
  const {
    clients,
    loaded: clientsLoaded,
    selectedClientId: clientId,
  } = useClientContext();
  const [list, setList] = useState<Employee[]>([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [showInactive, setShowInactive] = useState(false);
  const [undoId, setUndoId] = useState<string | null>(null);
  const [undoTimer, setUndoTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [form, setForm] = useState({
    dienstnehmernummer: "",
    first_name: "",
    last_name: "",
  });
  const [formErr, setFormErr] = useState<string | null>(null);
  // Welche Einstellungen dieser Mandant ueberhaupt hat — Entscheidung am
  // REGELWERK (dieselbe Sicht, die der Worker rechnet), nicht am Namen.
  // Thomas 14.08.: „nur Einstellungen die auch genutzt werden duerfen vorhanden
  // sein bei geo ... und bei zoescher dann ebenfalls anders rum."
  const [regelwerk, setRegelwerk] = useState<{ calc_model?: string | null; sollzeiten_required?: boolean; ueberstunden_weekly_threshold_h?: number } | null>(null);
  useEffect(() => {
    if (!clientId) { setRegelwerk(null); return; }
    let cancelled = false;
    api.clientRules(clientId)
      .then((r) => { if (!cancelled) setRegelwerk((r?.ruleset as any) ?? null); })
      .catch(() => { /* fail-soft: Bestandsstand */ });
    return () => { cancelled = true; };
  }, [clientId]);
  const spalten = mitarbeiterSpalten(regelwerk);
  const weeklyStandard = regelwerk?.ueberstunden_weekly_threshold_h;
  const showWeeklyThreshold = spalten.weeklyThreshold && typeof weeklyStandard === "number";

  const loadEmployees = async (cid: string) =>
    setList(await api.employees.list(cid));

  useEffect(() => {
    if (!clientId || clients.length === 0) {
      setList([]);
      return;
    }
    loadEmployees(clientId);
  }, [clientId, clients.length]);

  const onImport = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setMessage(null);
    try {
      const res = await api.employees.import(file, clientId);
      setMessage(`Importiert: ${res.imported}. Fehler: ${res.errors.length}`);
      await loadEmployees(clientId);
    } catch (err: any) {
      setMessage(`Fehler: ${err.message}`);
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const createOne = async () => {
    if (!form.dienstnehmernummer || !form.first_name || !form.last_name) {
      setFormErr("Dienstnehmernummer, Vorname und Nachname sind pflicht.");
      return;
    }
    setBusy(true);
    setFormErr(null);
    try {
      await api.employees.create({
        client_id: clientId,
        dienstnehmernummer: form.dienstnehmernummer,
        first_name: form.first_name,
        last_name: form.last_name,
      });
      setForm({ dienstnehmernummer: "", first_name: "", last_name: "" });
      setAddOpen(false);
      setMessage("Mitarbeiter angelegt.");
      await loadEmployees(clientId);
    } catch (err: any) {
      setFormErr(err.message);
    } finally {
      setBusy(false);
    }
  };

  const deactivateOne = async (emp: Employee) => {
    if (!confirm(`„${emp.first_name} ${emp.last_name}" deaktivieren?\nBestehende Karten bleiben erhalten.`)) return;
    await api.employees.patch(emp.id, { active: false } as any);
    setUndoId(emp.id);
    if (undoTimer) clearTimeout(undoTimer);
    setUndoTimer(setTimeout(() => setUndoId(null), 5000));
    await loadEmployees(clientId);
  };

  const reactivate = async (id: string) => {
    await api.employees.patch(id, { active: true } as any);
    setUndoId(null);
    await loadEmployees(clientId);
  };

  const bulkDeactivate = async () => {
    const names = list.filter((e) => selected.has(e.id)).map((e) => `${e.first_name} ${e.last_name}`);
    if (!confirm(`${names.length} Mitarbeiter deaktivieren?\n\n${names.join("\n")}`)) return;
    for (const id of selected) {
      await api.employees.patch(id, { active: false } as any);
    }
    setSelected(new Set());
    await loadEmployees(clientId);
  };

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const activeIds = list.filter((e) => e.active).map((e) => e.id);
    if (selected.size === activeIds.length) setSelected(new Set());
    else setSelected(new Set(activeIds));
  };

  const displayList = showInactive ? list : list.filter((e) => e.active);
  // Mandatory-Schedule-Check: aktive Mitarbeiter ohne work_schedule können
  // nicht für Karten-Zuordnung verwendet werden.
  const missingSchedule = list.filter((e) => e.active && !e.work_schedule);

  if (clientsLoaded && clients.length === 0) {
    return (
      <div className="empty-state">
        <h3>Kein aktiver Mandant</h3>
        <p className="muted">
          Mitarbeiter werden immer einem Mandanten zugeordnet. Zurzeit ist kein Mandant aktiv —
          bitte zuerst im Mandanten-Tab einen anlegen oder einen archivierten reaktivieren.
        </p>
        {props.onGoToClients && (
          <button className="primary" onClick={props.onGoToClients}>
            Zum Mandanten-Tab
          </button>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="toolbar">
        {/* T05b: Mandant-Auswahl lebt EINMAL in der Settings-Nav (Thomas 09.07.:
            zweiter Selector hier war redundant) — clientId kommt aus dem Context. */}
        <button className="primary" onClick={() => setAddOpen(!addOpen)}>
          {addOpen ? "Abbrechen" : "Mitarbeiter hinzufügen"}
        </button>
        <label className="upload-button">
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={onImport}
            disabled={busy}
            style={{ display: "none" }}
          />
          <span>{busy ? "Importiere…" : "CSV importieren"}</span>
        </label>
        <label className="field-inline">
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          <span>Inaktive anzeigen</span>
        </label>
        {message && <span className="muted">{message}</span>}
      </div>

      {/* Der Fehlalarm, der bei Geo alle 94 Namen aufzaehlte: Ohne
          Sollzeiten-Pflicht ist „ohne Soll-Zeiten" kein Mangel, sondern der
          vorgesehene Zustand — ihr Regelwerk sagt woertlich „die Mitarbeiter
          haben by design KEINE Wochenarbeitszeit im System" (Karte 587). Der
          Satz „koennen NICHT zugeordnet werden" stimmte dort ebenfalls nicht:
          zugeordnet wird bei 3G ueber den Blattkopf. */}
      {spalten.sollzeiten && missingSchedule.length > 0 && (
        <div className="warning-box" style={{ marginBottom: 12 }}>
          <strong>⚠ {missingSchedule.length} Mitarbeiter ohne Soll-Zeiten</strong> —
          können NICHT bei Karten-Upload zugeordnet werden, weil die Soll-Zeit-Konfiguration
          zwingend (Mandatory) ist für die Lohn-Berechnung.
          Bitte Arbeitszeitmodell pro Mitarbeiter eintragen (Mo–So in der Tabelle unten):
          {" "}
          <strong>{missingSchedule.map(e => `${e.last_name}, ${e.first_name}`).join(" · ")}</strong>
        </div>
      )}

      {selected.size > 0 && (
        <div className="bulk-bar">
          <strong>{selected.size} ausgewählt</strong>
          <button className="danger-btn" onClick={bulkDeactivate}>
            Ausgewählte deaktivieren
          </button>
          <button className="link" onClick={() => setSelected(new Set())}>Auswahl aufheben</button>
        </div>
      )}

      {undoId && (
        <div className="undo-toast">
          Mitarbeiter deaktiviert.{" "}
          <button className="link" onClick={() => reactivate(undoId)}>Rückgängig</button>
        </div>
      )}

      {addOpen && (
        <div className="kv-form">
          {formErr && <div className="error">{formErr}</div>}
          <label className="field">
            <span>Dienstnehmernummer</span>
            <input
              type="text"
              value={form.dienstnehmernummer}
              onChange={(e) => setForm({ ...form, dienstnehmernummer: e.target.value })}
            />
          </label>
          <label className="field">
            <span>Vorname</span>
            <input
              type="text"
              value={form.first_name}
              onChange={(e) => setForm({ ...form, first_name: e.target.value })}
            />
          </label>
          <label className="field">
            <span>Nachname</span>
            <input
              type="text"
              value={form.last_name}
              onChange={(e) => setForm({ ...form, last_name: e.target.value })}
            />
          </label>
          <button className="primary" disabled={busy} onClick={createOne}>
            {busy ? "Speichere…" : "Anlegen"}
          </button>
          <p className="muted small">
            Gleiche Dienstnehmernummer beim selben Mandant überschreibt bestehenden Eintrag
            (Reaktivierung bei inaktiven).
          </p>
        </div>
      )}

      <table className="data-table">
        <thead>
          <tr>
            <th style={{ width: 36 }}>
              <input type="checkbox"
                checked={selected.size > 0 && selected.size === displayList.filter((e) => e.active).length}
                onChange={toggleSelectAll}
                title="Alle auswählen"
              />
            </th>
            <th>DN-Nr.</th>
            <th>Nachname</th>
            <th>Vorname</th>
            {spalten.sollzeiten && (
              <>
                <th>Soll-Zeiten Mo–Sa <span style={{ fontWeight: 400, color: "#666" }}>(h/Tag)</span></th>
                <th>Σ h/Woche</th>
              </>
            )}
            {spalten.payMode && (
              <th title="50%-Überstunden: cash=Auszahlung, za=Zeitausgleich-Konto. Mehrstunden gehen immer aufs ZA-Konto.">Ü-50% Modus</th>
            )}
            {showWeeklyThreshold && <th id="weekly-threshold">Wochen-Überstundengrenze</th>}
            {/* KARTE 725: Überstundenpauschale laut Vertrag — eigene Spalte, nicht
                in „Ü-50% Modus" hineingedeutet. Die beiden beantworten
                verschiedene Fragen: der Modus, WIE ausbezahlt wird (ZA-Konto des
                Bestandsmandanten); diese Spalte, OB Überstunden überhaupt
                abzurechnen sind (Zulagenblatt, 3g). */}
            {spalten.pauschale && (
              <th title="Überstundenpauschale laut Vertrag: bei Ja bleiben Üst 50/100 aus dem Export. Solange nichts hinterlegt ist, hält eine Karte mit Überstunden an.">Ü-Pauschale</th>
            )}
            <th style={{ width: 50 }}></th>
          </tr>
        </thead>
        <tbody>
          {displayList.length === 0 ? (
            <tr><td colSpan={4 + (spalten.sollzeiten ? 2 : 0) + (spalten.payMode ? 1 : 0) + (spalten.pauschale ? 1 : 0) + (showWeeklyThreshold ? 1 : 0)} className="muted">Keine Mitarbeiter für diesen Mandanten.</td></tr>
          ) : displayList.map((e) => (
            <tr key={e.id} className={e.active ? "" : "row-inactive"}>
              <td>
                {e.active ? (
                  <input type="checkbox" checked={selected.has(e.id)} onChange={() => toggleSelect(e.id)} />
                ) : null}
              </td>
              <td>{e.dienstnehmernummer}</td>
              <td>{e.last_name}</td>
              <td>{e.first_name}</td>
              {spalten.sollzeiten && (
                <>
                  <td>
                    <ScheduleEditor employee={e} onChange={() => loadEmployees(clientId)} />
                  </td>
                  <td className="schedule-sum">
                    <ScheduleSum schedule={e.work_schedule} />
                  </td>
                </>
              )}
              {spalten.payMode && (
                <td>
                  <PayModeSelect employee={e} onChange={() => loadEmployees(clientId)} />
                </td>
              )}
              {showWeeklyThreshold && <td><WeeklyThresholdEditor employee={e} standard={weeklyStandard!} clientId={clientId} onChange={() => loadEmployees(clientId)} /></td>}
              {spalten.pauschale && (
                <td>
                  <PauschaleSelect employee={e} onChange={() => loadEmployees(clientId)} />
                </td>
              )}
              <td>
                {e.active ? (
                  <button className="icon-btn danger" title="Deaktivieren" onClick={() => deactivateOne(e)}>🗑</button>
                ) : (
                  <button className="icon-btn" title="Reaktivieren" onClick={() => reactivate(e.id)}>↩</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="help-block">
        <strong>CSV-Format:</strong> Semikolon-getrennt, Header-Zeile mit Spalten:
        <code>Dienstnehmernummer;Vorname;Nachname</code>.
        Import läuft auf den aktuell gewählten Mandant.
      </div>
    </div>
  );
}

function WeeklyThresholdEditor({ employee, standard, clientId, onChange }: {
  employee: Employee; standard: number; clientId: string; onChange: () => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [inherit, setInherit] = useState(employee.weekly_ueberstunden_threshold_h == null);
  const [hours, setHours] = useState(String(employee.weekly_ueberstunden_threshold_h ?? standard));
  const [fromMonth, setFromMonth] = useState(employee.weekly_threshold_from_month ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const begin = () => {
    setInherit(employee.weekly_ueberstunden_threshold_h == null);
    setHours(String(employee.weekly_ueberstunden_threshold_h ?? standard));
    setFromMonth(employee.weekly_threshold_from_month ?? "");setError(null);setOpen(true);
  };
  const save = async () => {
    const value = Number(hours);
    if (!inherit && (!hours.trim() || !Number.isFinite(value) || value <= 0 || value > 168)) { setError("Bitte einen gültigen Stundenwert größer 0 und höchstens 168 eingeben."); return; }
    setBusy(true);setError(null);
    try { await api.employees.changeWeeklyThreshold(employee.id, clientId, inherit ? null : value, fromMonth || null); await onChange();setOpen(false); }
    catch (e: any) { setError(e.message); }
    finally { setBusy(false); }
  };
  if (!open) return <button className="link" onClick={begin} aria-label={`Wochen-Überstundengrenze für ${employee.first_name} ${employee.last_name} bearbeiten`}>
    {employee.weekly_ueberstunden_threshold_h ?? standard} h · {employee.weekly_ueberstunden_threshold_h == null ? "Standard" : "Individuell"}
    {employee.weekly_threshold_from_month && ` · ab ${employee.weekly_threshold_from_month}`}
  </button>;
  return <div style={{ minWidth: 230, padding: 8 }}>
    <label style={{ display: "block" }}><input type="checkbox" checked={inherit} onChange={e => setInherit(e.target.checked)} disabled={busy} /> Standard verwenden ({standard} h)</label>
    {!inherit && <label style={{ display: "block", marginTop: 6 }}>Individuelle Grenze (h/Woche)<input aria-label="Individuelle Wochen-Überstundengrenze" type="number" min="0.01" max="168" step="0.01" value={hours} onChange={e => setHours(e.target.value)} disabled={busy} style={{ width: 90, marginLeft: 6 }} /></label>}
    <label style={{ display: "block", marginTop: 6 }}>Gültig ab Monat<input aria-label="Wochen-Überstundengrenze gültig ab Monat" type="month" value={fromMonth} onChange={e => setFromMonth(e.target.value)} disabled={busy} /></label>
    <div className="muted" style={{ fontSize: 11, margin: "6px 0" }}>Ohne Monat: ab Beginn. Sollzeit und Tagesgrenze bleiben unverändert. Bestehende Berechnungen werden nicht automatisch überschrieben.</div>
    {error && <div role="alert" className="error">{error}</div>}
    <button onClick={save} disabled={busy}>{busy ? "Speichert…" : "Speichern"}</button>{" "}
    <button onClick={() => setOpen(false)} disabled={busy}>Abbrechen</button>{" "}
    {!inherit && <button className="link" onClick={() => setInherit(true)} disabled={busy}>Auf Standard zurücksetzen</button>}
  </div>;
}


// ─── ScheduleEditor: Pattern (weekly/biweekly) + Mo–Sa pro Woche ────────────
//
// Schema (employees.work_schedule jsonb):
//   { pattern: "weekly" | "biweekly",
//     week_a: { mo, di, mi, do, fr, sa },
//     week_b: same | null,
//     soll_h_woche_a, soll_h_woche_b, notes, imported_from }
//
// Biweekly = gerade vs ungerade Woche unterschiedlich (z.B. "jeden 2. Samstag
// frei"). Bei pattern="biweekly" werden ZWEI Reihen Inputs gezeigt.

const DAYS_DE: readonly { key: keyof DayHours; label: string }[] = [
  { key: "mo", label: "Mo" },
  { key: "di", label: "Di" },
  { key: "mi", label: "Mi" },
  { key: "do", label: "Do" },
  { key: "fr", label: "Fr" },
  { key: "sa", label: "Sa" },
] as const;

function sumDayHours(d: DayHours | null | undefined): number {
  if (!d) return 0;
  return DAYS_DE.reduce((s, { key }) => s + (Number(d[key]) || 0), 0);
}

function ScheduleSum({ schedule }: { schedule: WorkSchedule | null }) {
  if (!schedule) return <span className="muted">—</span>;
  const a = sumDayHours(schedule.week_a);
  const b = schedule.pattern === "biweekly" ? sumDayHours(schedule.week_b) : null;
  if (b !== null) {
    return (
      <span title="Woche A / Woche B">
        {a.toFixed(1)} / {b.toFixed(1)}
      </span>
    );
  }
  return <>{a.toFixed(1)}</>;
}

function ScheduleEditor({ employee, onChange }: { employee: Employee; onChange: () => void }) {
  const sched = employee.work_schedule;
  const pattern = sched?.pattern ?? "weekly";

  const patchSchedule = async (next: WorkSchedule | null) => {
    await api.employees.patch(employee.id, { work_schedule: next as any });
    onChange();
  };

  const togglePattern = async () => {
    const nextPattern = pattern === "weekly" ? "biweekly" : "weekly";
    const base: WorkSchedule = {
      pattern: nextPattern,
      week_a: sched?.week_a ?? {},
      week_b: nextPattern === "biweekly" ? (sched?.week_b ?? { ...(sched?.week_a ?? {}) }) : null,
      notes: sched?.notes ?? null,
      imported_from: sched?.imported_from ?? null,
      imported_at: sched?.imported_at ?? null,
    };
    base.soll_h_woche_a = sumDayHours(base.week_a);
    base.soll_h_woche_b = base.week_b ? sumDayHours(base.week_b) : null;
    await patchSchedule(base);
  };

  const updateDay = async (week: "a" | "b", day: keyof DayHours, value: number | undefined) => {
    const wa = { ...(sched?.week_a ?? {}) };
    const wb = sched?.week_b ? { ...sched.week_b } : null;
    if (week === "a") {
      if (value === undefined) delete wa[day]; else wa[day] = value;
    } else if (wb) {
      if (value === undefined) delete wb[day]; else wb[day] = value;
    }
    const next: WorkSchedule = {
      pattern,
      week_a: wa,
      week_b: pattern === "biweekly" ? wb : null,
      notes: sched?.notes ?? null,
      imported_from: sched?.imported_from ?? null,
      imported_at: sched?.imported_at ?? null,
      soll_h_woche_a: sumDayHours(wa),
      soll_h_woche_b: pattern === "biweekly" && wb ? sumDayHours(wb) : null,
    };
    // If everything is empty + no pattern set, clear the schedule entirely
    const totalA = sumDayHours(wa);
    const totalB = wb ? sumDayHours(wb) : 0;
    const shouldClear = totalA === 0 && totalB === 0 && pattern === "weekly";
    await patchSchedule(shouldClear ? null : next);
  };

  const renderRow = (week: "a" | "b", days: DayHours | null | undefined) => (
    <div className="schedule-row" key={week}>
      {pattern === "biweekly" && (
        <span style={{
          minWidth: 28, fontSize: 11, fontWeight: 600,
          color: week === "a" ? "#2563eb" : "#7c3aed",
        }} title={week === "a" ? "Ungerade Woche" : "Gerade Woche"}>
          {week === "a" ? "A" : "B"}
        </span>
      )}
      {DAYS_DE.map(({ key, label }) => (
        <input
          key={key}
          type="number" step="0.05" min="0" max="24"
          className="inline-num schedule-day"
          defaultValue={(days as any)?.[key] ?? ""}
          placeholder="–"
          title={label}
          onBlur={(ev) => {
            const v = ev.target.value ? Number(ev.target.value) : undefined;
            const cur = (days as any)?.[key];
            if (v === cur) return;
            void updateDay(week, key, v);
          }}
        />
      ))}
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 11 }}>
        <button
          type="button"
          onClick={togglePattern}
          title={pattern === "weekly"
            ? "Auf gerade/ungerade Woche umschalten"
            : "Auf wöchentlich-gleich zurücksetzen"}
          style={{
            padding: "2px 8px", fontSize: 11, borderRadius: 3, cursor: "pointer",
            background: pattern === "biweekly" ? "#7c3aed" : "#e5e7eb",
            color: pattern === "biweekly" ? "#fff" : "#374151",
            border: "1px solid",
            borderColor: pattern === "biweekly" ? "#6d28d9" : "#d1d5db",
          }}>
          {pattern === "weekly" ? "1-wöchig" : "2-wöchig (gerade/ungerade)"}
        </button>
        {sched?.notes && (
          <span style={{ fontSize: 11, color: "#92400e", fontStyle: "italic" }} title={sched.notes}>
            ⚠ {sched.notes.slice(0, 50)}{sched.notes.length > 50 ? "…" : ""}
          </span>
        )}
        {sched?.imported_from && (
          <span style={{ fontSize: 10, color: "#9ca3af" }} title={`Aus ${sched.imported_from}`}>
            (importiert)
          </span>
        )}
      </div>
      {renderRow("a", sched?.week_a)}
      {pattern === "biweekly" && renderRow("b", sched?.week_b)}
    </div>
  );
}


/**
 * KARTE 725 — Überstundenpauschale je Dienstnehmer, DREIWERTIG.
 *
 * `null` ist hier ein echter Zustand, nicht ein leeres Feld: Solange niemand
 * geantwortet hat, HÄLT eine Zulagenblatt-Karte mit Überstunden an, statt sie
 * still aus dem Export zu lassen (Thomas 14.08.: „stoppen wir die validierung
 * und darf nicht weiter bis das konfiguriert ist").
 *
 * Deshalb steht „— nicht hinterlegt —" als eigene Auswahl da und nicht als
 * unsichtbarer Vorgabewert: Der Mensch soll sehen, dass die Frage offen ist.
 */
function PauschaleSelect({ employee, onChange }: { employee: Employee; onChange: () => void }) {
  const [busy, setBusy] = useState(false);
  const current = employee.ueberstunden_pauschaliert;
  const wert = current === true ? "ja" : current === false ? "nein" : "offen";
  async function set(next: string) {
    if (next === wert) return;
    setBusy(true);
    try {
      await api.employees.patch(employee.id, {
        ueberstunden_pauschaliert: next === "ja" ? true : next === "nein" ? false : null,
      } as any);
      onChange();
    } finally { setBusy(false); }
  }
  return (
    <select
      value={wert}
      onChange={(ev) => set(ev.target.value)}
      disabled={busy}
      title="Überstundenpauschale laut Vertrag. ja = Üst 50/100 bleiben aus dem Export; nein = sie gehen mit; nicht hinterlegt = Karten mit Überstunden halten an."
      style={{ fontSize: 12, ...(wert === "offen" ? { borderColor: "#b45309", color: "#b45309" } : {}) }}
    >
      <option value="offen">— nicht hinterlegt —</option>
      <option value="ja">Ja (pauschaliert)</option>
      <option value="nein">Nein</option>
    </select>
  );
}

function PayModeSelect({ employee, onChange }: { employee: Employee; onChange: () => void }) {
  const [busy, setBusy] = useState(false);
  const current = employee.ueberstunden_pay_mode;
  type Mode = "cash" | "za" | "pauschale";
  async function set(next: Mode) {
    if (next === current) return;
    setBusy(true);
    try {
      await api.employees.patch(employee.id, { ueberstunden_pay_mode: next } as any);
      onChange();
    } finally { setBusy(false); }
  }
  return (
    <select
      value={current}
      onChange={(ev) => set(ev.target.value as Mode)}
      disabled={busy}
      title="50%-Überstunden: cash=Auszahlung in RZL, za=Zeitausgleich-Konto, pauschale=durch Vertragspauschale abgedeckt (Std + ZA werden im CSV als 0 exportiert)"
      style={{ fontSize: 12 }}
    >
      <option value="za">ZA (Konto)</option>
      <option value="cash">Cash (Auszahlung)</option>
      <option value="pauschale">Pauschale (vertraglich)</option>
    </select>
  );
}
